import { useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Target } from 'lucide-react';

export default function ManualScoreTab({ 
  users, 
  existingScorecards,
  onScoreCreated 
}) {
  const [manualScore, setManualScore] = useState({
    scorecard_date: '',
    player_name: '',
    division: '',
    points: '',
    score: '',
    existing_scorecard_id: '',
    auto_calculate: false
  });
  const [customDivision, setCustomDivision] = useState('');
  const [showCustomDivision, setShowCustomDivision] = useState(false);
  const [addToExisting, setAddToExisting] = useState(false);

  const handleManualScoreEntry = async (e) => {
    e.preventDefault();
    
    // Use custom division if "other" is selected
    const divisionToUse = showCustomDivision ? customDivision : manualScore.division;
    
    if (!divisionToUse) {
      toast.error('Please select or enter a division');
      return;
    }

    // Validate based on mode
    if (addToExisting) {
      if (!manualScore.existing_scorecard_id) {
        toast.error('Please select a match to add to');
        return;
      }
    } else {
      if (!manualScore.points && manualScore.points !== 0) {
        toast.error('Please enter points for standalone entry');
        return;
      }
    }
    
    try {
      const payload = {
        scorecard_date: manualScore.scorecard_date,
        player_name: manualScore.player_name,
        division: divisionToUse,
        score: parseInt(manualScore.score) || 0
      };

      if (addToExisting) {
        payload.existing_scorecard_id = manualScore.existing_scorecard_id;
        payload.auto_calculate = manualScore.auto_calculate;
        // If not auto-calculating, we need points
        if (!manualScore.auto_calculate) {
          payload.points = parseInt(manualScore.points) || 0;
        }
      } else {
        payload.points = parseInt(manualScore.points);
      }

      await axios.post(`${API}/admin/scores/manual-entry`, payload);
      
      const message = addToExisting 
        ? `Player added to match${manualScore.auto_calculate ? ' and points recalculated!' : '!'}`
        : 'Points entered successfully!';
      toast.success(message);
      
      setManualScore({ 
        scorecard_date: '', 
        player_name: '', 
        division: '', 
        points: '',
        score: '',
        existing_scorecard_id: '',
        auto_calculate: false
      });
      setCustomDivision('');
      setShowCustomDivision(false);
      setAddToExisting(false);
      
      if (onScoreCreated) {
        onScoreCreated();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enter points');
    }
  };

  return (
    <div className="bg-white border border-stone-200 rounded-sm p-8 shadow-sm max-w-2xl">
      <h2 className="font-barlow font-bold text-2xl text-slate-900 uppercase tracking-tight mb-2">
        Manual Score Entry
      </h2>
      <p className="text-stone-600 font-inter text-sm mb-6">
        Manually enter a score without uploading a file
      </p>
      
      <form onSubmit={handleManualScoreEntry} className="space-y-4">
        {/* Mode Selection */}
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 mb-4">
          <Label className="font-inter font-medium text-slate-900 mb-3 block">Entry Mode</Label>
          <div className="flex flex-col gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="entryMode"
                data-testid="entry-mode-standalone"
                checked={!addToExisting}
                onChange={() => {
                  setAddToExisting(false);
                  setManualScore({...manualScore, existing_scorecard_id: '', auto_calculate: false});
                }}
                className="w-4 h-4 text-amber-600"
              />
              <span className="font-inter text-sm">
                <strong>Standalone entry</strong> - Create new scorecard with direct points
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="entryMode"
                data-testid="entry-mode-existing"
                checked={addToExisting}
                onChange={() => setAddToExisting(true)}
                className="w-4 h-4 text-amber-600"
              />
              <span className="font-inter text-sm">
                <strong>Add to existing match</strong> - Add player to a match and recalculate points
              </span>
            </label>
          </div>
        </div>

        {/* Existing Match Selection (only shown when adding to existing) */}
        {addToExisting && (
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
            <Label htmlFor="existing_match" className="font-inter font-medium text-slate-900">Select Match</Label>
            <Select 
              value={manualScore.existing_scorecard_id}
              onValueChange={(value) => {
                const selected = existingScorecards.find(s => s.id === value);
                setManualScore({
                  ...manualScore, 
                  existing_scorecard_id: value,
                  scorecard_date: selected?.scorecard_date || ''
                });
              }}
            >
              <SelectTrigger className="mt-1 bg-white border-amber-300 rounded-sm h-12 font-inter">
                <SelectValue placeholder="Select a match to add player to" />
              </SelectTrigger>
              <SelectContent className="bg-white border-stone-200 rounded-sm max-h-60">
                {existingScorecards
                  .sort((a, b) => (b.scorecard_date || '').localeCompare(a.scorecard_date || ''))
                  .map(sc => (
                    <SelectItem key={sc.id} value={sc.id}>
                      {sc.scorecard_date || 'No date'} - {sc.file_name} ({sc.players?.length || 0} players)
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>

            <div className="mt-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="auto-recalculate-checkbox"
                  checked={manualScore.auto_calculate}
                  onChange={(e) => setManualScore({...manualScore, auto_calculate: e.target.checked})}
                  className="w-4 h-4 text-amber-600 rounded"
                />
                <span className="font-inter text-sm">
                  <strong>Auto-recalculate all points</strong> - Re-rank all players in the match by score
                </span>
              </label>
              <p className="text-xs text-amber-700 mt-1 ml-6">
                When enabled, all players&apos; points will be recalculated based on their scores within each division.
              </p>
            </div>
          </div>
        )}

        {/* Date (hidden when adding to existing match since it's auto-filled) */}
        {!addToExisting && (
          <div>
            <Label htmlFor="scorecard_date" className="font-inter font-medium text-slate-900">Competition Date</Label>
            <Input
              id="scorecard_date"
              type="date"
              value={manualScore.scorecard_date}
              onChange={(e) => setManualScore({...manualScore, scorecard_date: e.target.value})}
              className="mt-1 bg-white border-stone-300 rounded-sm h-12 font-inter"
              required
            />
          </div>
        )}

        <div>
          <Label htmlFor="player_name" className="font-inter font-medium text-slate-900">Shooter</Label>
          <Select 
            value={manualScore.player_name}
            onValueChange={(value) => setManualScore({...manualScore, player_name: value})}
          >
            <SelectTrigger className="mt-1 bg-white border-stone-300 rounded-sm h-12 font-inter">
              <SelectValue placeholder="Select a registered shooter" />
            </SelectTrigger>
            <SelectContent className="bg-white border-stone-200 rounded-sm">
              {users.map(u => (
                <SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="division" className="font-inter font-medium text-slate-900">Division</Label>
          <Select
            value={showCustomDivision ? 'custom' : manualScore.division}
            onValueChange={(value) => {
              if (value === 'custom') {
                setShowCustomDivision(true);
                setManualScore({...manualScore, division: ''});
              } else {
                setShowCustomDivision(false);
                setCustomDivision('');
                setManualScore({...manualScore, division: value});
              }
            }}
          >
            <SelectTrigger className="mt-1 bg-white border-stone-300 rounded-sm h-12 font-inter">
              <SelectValue placeholder="Select division" />
            </SelectTrigger>
            <SelectContent className="bg-white border-stone-200 rounded-sm">
              <SelectItem value="A">Division A</SelectItem>
              <SelectItem value="EDC">EDC</SelectItem>
              <SelectItem value="PCC Optics">PCC Optics</SelectItem>
              <SelectItem value="Production">Production (Race Gun)</SelectItem>
              <SelectItem value="Standard">Standard (Race Gun)</SelectItem>
              <SelectItem value="Race Gun">Race Gun</SelectItem>
              <SelectItem value="C">Custom (C)</SelectItem>
              <SelectItem value="O">Open (O)</SelectItem>
              <SelectItem value="R">Revolver (R)</SelectItem>
              <SelectItem value="custom" className="font-semibold text-amber-600">
                + Create New Division
              </SelectItem>
            </SelectContent>
          </Select>
          
          {showCustomDivision && (
            <div className="mt-3">
              <Label htmlFor="custom-division" className="font-inter font-medium text-slate-900 text-sm">
                New Division Name
              </Label>
              <Input
                id="custom-division"
                type="text"
                value={customDivision}
                onChange={(e) => setCustomDivision(e.target.value)}
                className="mt-1 bg-amber-50 border-amber-300 rounded-sm h-12 font-inter"
                placeholder="e.g., Limited, Classic, etc."
                required={showCustomDivision}
              />
              <p className="text-xs text-stone-500 mt-1 font-inter">
                This division will be created and used for this score entry
              </p>
            </div>
          )}
        </div>

        {/* Score (for ranking in existing match) */}
        {addToExisting && (
          <div>
            <Label htmlFor="score" className="font-inter font-medium text-slate-900">
              Score {manualScore.auto_calculate && <span className="text-amber-600">*</span>}
            </Label>
            <Input
              id="score"
              type="number"
              value={manualScore.score}
              onChange={(e) => setManualScore({...manualScore, score: e.target.value})}
              className="mt-1 bg-white border-stone-300 rounded-sm h-12 font-inter font-mono"
              placeholder="0"
              required={addToExisting && manualScore.auto_calculate}
            />
            <p className="text-xs text-stone-500 mt-1">
              {manualScore.auto_calculate 
                ? "Enter the raw score - this will be used to rank the player within the division"
                : "Optional - used for record keeping"}
            </p>
          </div>
        )}

        {/* Points (required for standalone, optional for existing match without auto-calc) */}
        {(!addToExisting || !manualScore.auto_calculate) && (
          <div>
            <Label htmlFor="points" className="font-inter font-medium text-slate-900">Points</Label>
            <Input
              id="points"
              type="number"
              value={manualScore.points}
              onChange={(e) => setManualScore({...manualScore, points: e.target.value})}
              className="mt-1 bg-white border-stone-300 rounded-sm h-12 font-inter font-mono"
              required={!addToExisting || !manualScore.auto_calculate}
              min="-100"
              max="100"
              placeholder="0"
            />
            <p className="text-xs text-stone-500 mt-1">
              {addToExisting && !manualScore.auto_calculate
                ? "Enter points directly (other players' points won't change)"
                : "Enter points to award (use negative for penalties, e.g., -5)"}
            </p>
          </div>
        )}

        <Button
          type="submit"
          className="w-full bg-amber-600 text-white hover:bg-amber-700 rounded-sm uppercase tracking-wider font-bold h-12 font-barlow"
        >
          <Target className="w-5 h-5 mr-2" strokeWidth={1.5} />
          {addToExisting ? 'Add to Match' : 'Submit Points'}
        </Button>
      </form>
    </div>
  );
}
